import { Injectable, signal, computed, inject, effect, untracked } from '@angular/core';
import { DatePipe } from '@angular/common';
import { DateUtil } from '../utils/date.util';
import { WebhookRetryService } from './webhook-retry.service';
import { supabase } from '../supabase';
import { createClient } from '@supabase/supabase-js';
import { environment } from '../environments/environment';
import {
  SystemLog, TipoDocumento, TipoAcao, ProtocoloFiscal, HistoricoAuditoria,
  QuotaPool, QuotaStat, SurgeryStepConfig, Executor, AgendaSlot,
  ProcedureLocation, Procedure, ProcedureQuota, PatientHistoryItem,
  Operator, Patient, ACS, Appointment, Municipality, PatientProcess, UserRole
} from '../models';

export * from '../models';

// --- Supabase Integration ---

import { LoadingService } from './loading.service';
import { ToastService } from './toast.service';
import { AuditService } from './audit.service';
import { OperatorService } from './operator.service';
import { MunicipalityService } from './municipality.service';
import { PatientService } from './patient.service';
import { AppointmentService } from './appointment.service';
import { QuotaService, DistributeProgressInfo } from './quota.service';
export type { DistributeProgressInfo } from './quota.service';
// import { DownloadService } from './download.service'; // Decoupled for performance
import { AcsService } from './acs.service';
import { ProfessionalService } from './professional.service';

export function normalizeCategory(cat: string | null | undefined): string {
  if (!cat) return '';
  let s = cat.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();

  // Detecção resiliente de categorias críticas com problemas de encoding
  if (s.includes('CONSULTA') && (s.includes('BASICA') || s.includes('B SICA') || (s.includes('B') && s.includes('SICA')))) {
    return 'CONSULTA BASICA';
  }
  if (s.includes('CONSULTA') && s.includes('ESPECIALIZADA')) {
    return 'CONSULTA ESPECIALIZADA';
  }

  return s
    .replace(/[^A-Z0-9\s]/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

@Injectable({
  providedIn: 'root'
})
export class DatabaseService {
  private static readonly APPOINTMENT_COLUMNS = 'id, patient_id, procedure_id, municipality_id, status, priority, registration_date, request_date, request_image_url, schedule_date, schedule_time, schedule_location, outcome_date, outcome_location, outcome_reason, judicial_process_number, judicial_origin, urgency_priority, latest_protocol, pre_surgery_completed, urgency_justification, anatomical_region, audit_trail, parent_appointment_id, is_surgery_parent, sub_protocol, current_step_index, executor_id, executor_name, allocated_professional_id, allocated_date, allocated_time, scheduling_started_at';

  public loadingService = inject(LoadingService);
  private toast = inject(ToastService);
  private patientService = inject(PatientService);
  public apptService = inject(AppointmentService);
  private quotaService = inject(QuotaService);
  // private downloadService = inject(DownloadService); // Decoupled
  private auditService = inject(AuditService);
  private operatorService = inject(OperatorService);
  private municipalityService = inject(MunicipalityService);
  private acsService = inject(AcsService);
  private profService = inject(ProfessionalService);
  private datePipe = new DatePipe('en-US');
  private retryService = inject(WebhookRetryService);

  // --- Redirecting Signals for Backward Compatibility ---
  public proceduresTable = this.quotaService.proceduresTable;
  private procedureQuotasTable = this.quotaService.procedureQuotasTable;
  public patientsTable = this.patientService.patientsTable;
  public totalPatients = this.patientService.totalPatients;
  public appointmentsTable = this.apptService.appointmentsTable;
  public quotaPoolsTable = this.quotaService.quotaPoolsTable;

  public operatorsTable = this.operatorService.operatorsTable;
  public municipalitiesTable = this.municipalityService.municipalitiesTable;
  public locationsTable = this.quotaService.locationsTable;
  public executorsTable = this.quotaService.executorsTable;
  public acsTable = this.acsService.acsTable;
  public acsTeamsTable = this.acsService.acsTeamsTable;
  public professionalsTable = this.profService.professionalsTable;

  // Initialize logs by proxying from AuditService
  public systemLogsTable = this.auditService.systemLogsTable;
  public queueRefreshTrigger = this.apptService.queueRefreshTrigger;

  // Global filter state for dashboards
  dashboardDate = signal<Date>(new Date(), {
    equal: (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  });
  isDashboardStatsLoading = signal<boolean>(false);
  hasLoadedStatsOnce = signal<boolean>(false);
  loadingQuotas = signal<boolean>(false);
  dashboardDayFilter = signal<number | null>(null); // NEW: Day Filter
  dashboardProcedureFilter = signal<number | null>(null);
  dashboardCategoryFilter = signal<string | null>(null); // NEW: Category Filter
  dashboardMunicipalityFilter = signal<string | null>(null); // NEW: Municipality Filter
  dashboardExecutorFilter = signal<string | null>(null); // NEW: Executor Filter
  dashboardLocationFilter = signal<string | null>(null); // NEW: Location Filter
  dashboardAcsFilter = signal<string | null>(null); // NEW: ACS Filter
  procedureMonth = signal<Date>(new Date()); // NEW: Month filter for procedures and quota pools

  // Ported Drill-down Signals
  drillDownSearch = signal('');
  drillDownFilterStatus = signal<string | string[] | null>(null);
  drillDownAcsFilter = signal<string | null>(null);
  drillDownTitle = signal('Lista de Pacientes');
  drillDownType = signal<'waiting' | 'scheduled' | 'done' | 'canceled' | 'patients' | 'appointments'>('waiting');
  dashboardView = signal<'STATS' | 'PATIENTS_FULL' | 'PENDING_REALIZATIONS_FULL'>('STATS');

  // Proxy do quotaStatsData do QuotaService - sem duplicidade de estado
  private quotaStatsData = this.quotaService.quotaStatsData;

  public currentMunicipalityId = signal<string | null>(null);
  public currentOperator = signal<Operator | null>(null);
  public currentUserRole = signal<UserRole | null>(null);
  public isPublicMode = signal(false);

  public dashboardSummaryData = signal<{ waiting: number, scheduled: number, done: number, canceled: number, requests?: number, year_done?: number, pending_realizations?: number, total_patients?: number }>({
    waiting: 0, scheduled: 0, done: 0, canceled: 0, requests: 0, year_done: 0, pending_realizations: 0, total_patients: 0
  });

  constructor() {
    // Permitir que serviços acessem métodos de auditoria e auxiliares do DatabaseService
    this.patientService.setDatabaseService(this);
    this.apptService.setDatabaseService(this);

    // Efeito para recarregar estatísticas e metadados do dashboard quando filtros mudam
    effect(() => {
      // Proteção: Não carregar estatísticas se não houver usuário logado e não estiver em modo público
      if (!this.currentUserRole() && !this.isPublicMode()) {
        return;
      }

      const date = this.dashboardDate();
      let munId = this.dashboardMunicipalityFilter() || this.currentMunicipalityId();
      if (munId === 'null') munId = null;

      let category = this.dashboardCategoryFilter();
      if (category === 'null') category = null;

      let procedureId = this.dashboardProcedureFilter();
      if (String(procedureId) === 'null') procedureId = null;

      let acsId = this.dashboardAcsFilter();
      if (acsId === 'null') acsId = null;

      let executorId = this.dashboardExecutorFilter();
      if (executorId === 'null') executorId = null;

      let day = this.dashboardDayFilter();
      if (String(day) === 'null') day = null;

      // Extract excludeUbsCategories logic here
      let excludeUbsCategories = false;
      if (munId) {
        const mun = untracked(() => this.municipalitiesTable()).find(m => m.id === munId);
        // O Admin Global e o Municipal devem ver a mesma coisa. Se o município tem módulo UBS, o dashboard principal exclui as consultas básicas.
        if (mun?.enable_ubs_module) {
          excludeUbsCategories = true;
        }
      }

      // Recarrega estatísticas
      this.refreshDashboardStats(munId, date.getFullYear(), date.getMonth() + 1, {
        category: (category && category !== 'Todas as Categorias') ? category : undefined,
        procedureId: procedureId || undefined,
        acsId: acsId || undefined,
        executorId: executorId || undefined,
        excludeUbsCategories,
        day: day || undefined
      });

      // NOVO: Recarrega metadados se o município mudar para garantir filtros populados
      if (munId) {
        untracked(() => {
          this.refreshAcs(munId);
          this.refreshExecutors(munId);
          this.refreshLocations(munId);
        });

        // Carrega também os pools e stats de cotas para o município e mês selecionado
        const monthRef = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        untracked(() => {
          this.loadingQuotas.set(true);
          Promise.all([
            this.quotaService.refreshQuotaPools(munId, monthRef),
            this.loadQuotaStats(munId, monthRef)
          ]).catch(err => console.error('[DB] Error loading quotas for dashboard:', err))
            .finally(() => this.loadingQuotas.set(false));
        });
      } else {
        // Se não houver município (GLOBAL), limpa as cotas
        untracked(() => {
          this.quotaPoolsTable.set([]);
        });
      }
    });
  }

  public pendingRealizationsCountState = signal<number>(0);

  async refreshPendingRealizationsCount(municipalityId: string | null) {
    try {
      const todayStr = DateUtil.getBrasiliaNow().toISOString().split('T')[0];
      let query = supabase
        .from('appointments')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'Agendado')
        .lte('schedule_date', todayStr)
        .neq('is_active', false);

      if (municipalityId) {
        query = query.eq('municipality_id', municipalityId);
      }

      const { count, error } = await query;
      if (!error && count !== null) {
        this.pendingRealizationsCountState.set(count);
      }
    } catch (err) {
      console.warn('[DB] Error refreshing pending realizations count:', err);
    }
  }

  async refreshDashboardStats(municipalityId: string | null, year: number, month: number, filters?: { category?: string, procedureId?: number, acsId?: string, executorId?: string, excludeUbsCategories?: boolean, day?: number }) {
    this.isDashboardStatsLoading.set(true);
    try {
      this.refreshPendingRealizationsCount(municipalityId);
      const summary = await this.apptService.getDashboardSummary(municipalityId, year, month, filters);
      if (summary) {
        this.dashboardSummaryData.set(summary);
        if (summary.total_patients !== undefined && summary.total_patients !== null) {
          this.patientService.totalPatients.set(Number(summary.total_patients));
        }
        if (summary.pending_realizations !== undefined && summary.pending_realizations !== null && summary.pending_realizations > 0) {
          this.pendingRealizationsCountState.set(Number(summary.pending_realizations));
        }
        this.hasLoadedStatsOnce.set(true);
      }
    } catch (err) {
      console.warn('[DB] Error refreshing dashboard stats:', err);
    } finally {
      this.isDashboardStatsLoading.set(false);
    }
  }

  async fetchPendingRealizationsPaginated(params: {
    category?: string;
    acsId?: string;
    searchTerm?: string;
    procedureId?: number | null;
    location?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    limit: number;
    offset: number;
    excludeUbsCategories?: boolean;
    abortSignal?: AbortSignal;
    hideLoading?: boolean;
  }) {
    const munId = this.dashboardMunicipalityFilter() || this.currentMunicipalityId();
    return this.apptService.fetchPendingRealizationsPaginated(munId, params);
  }

  async fetchQueuePaginated(params: { procedureId?: number, category?: string, priority?: string, searchTerm?: string, locationId?: string, originLocation?: string, statusGroup?: string[], dateFilter?: string, acsId?: string, executorId?: string, limit: number, offset: number, forceRegistrationFilter?: boolean, orderByStatus?: boolean, excludeUbsCategories?: boolean, abortSignal?: AbortSignal, hideLoading?: boolean }) {
    // Usa dashboardMunicipalityFilter se disponível, senão usa currentMunicipalityId
    const munId = this.dashboardMunicipalityFilter() || this.currentMunicipalityId();
    return this.apptService.fetchQueuePaginated(munId, params);
  }

  async fetchAllQueueRecords(params: any) {
    const munId = this.dashboardMunicipalityFilter() || this.currentMunicipalityId();
    return this.apptService.fetchAllQueueRecords(munId, params);
  }

  /**
   * Obtém dados básicos do operador logado via Auth User ID
   * Usado para restauração de sessão.
   */
  async getOperatorByAuthId(authUserId: string) {
    const { data, error } = await supabase
      .from('operators')
      .select('*')
      .eq('auth_user_id', authUserId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    const operator = this.operatorService.mapDbToOperator(data);
    const { data: mun } = await supabase
      .from('municipalities')
      .select('id, name, is_active, logo_url')
      .eq('id', operator.municipalityId)
      .single();

    return { operator, municipality: mun };
  }

  // --- MAPPING HELPERS (Delegated to specialized services) ---

  public mapDbToAppointment(a: any): Appointment {
    return this.apptService.mapDbToAppointment(a);
  }

  public mapAppointmentToDb(a: Appointment): any {
    return this.apptService.mapAppointmentToDb(a);
  }

  public mapDbToPatient(p: any): Patient {
    return this.patientService.mapDbToPatient(p);
  }

  public mapPatientToDb(p: Patient): any {
    return this.patientService.mapPatientToDb(p);
  }

  public mapDbToLocation(l: any): ProcedureLocation {
    return this.quotaService.mapDbToLocation(l);
  }

  // TRACKING LOADED DATA
  private loadedMonths = new Set<string>(); // Format: YYYY-MM

  /**
   * Loads historical data for a specific month.
   * "Sob Demanda" logic.
   */
  async loadHistoricalData(date: Date) {
    const municipalityId = this.currentMunicipalityId();
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const key = `${yyyy}-${mm}`;

    if (this.loadedMonths.has(key)) {
      console.log(`[DB] Data for ${key} already loaded.`);
      return;
    }

    console.log(`[DB] Loading Historical Data for: ${key} `);

    // Usar strings de data pura para colunas DATE, evitando shifts de horário/timestamp
    const startIso = `${yyyy}-${mm}-01`;
    const lastDayObj = new Date(yyyy, date.getMonth() + 1, 0);
    const endIso = `${yyyy}-${mm}-${String(lastDayObj.getDate()).padStart(2, '0')}`;

    try {
      // Busca histórico do mês: registros com schedule_date OU outcome_date dentro do intervalo.
      // Usa filtro OR para capturar tanto agendamentos marcados quanto realizados/cancelados.
      let historyQuery = supabase.from('appointments').select(DatabaseService.APPOINTMENT_COLUMNS)
        .neq('is_active', false)
        .in('status', ['Agendado', 'Realizado', 'Não Realizado', 'Cancelado'])
        .or(
          `and(schedule_date.gte."${startIso}",schedule_date.lte."${endIso}"),` +
          `and(outcome_date.gte."${startIso}",outcome_date.lte."${endIso}")`
        );

      if (municipalityId) {
        historyQuery = historyQuery.eq('municipality_id', municipalityId);
      }

      const { data: historyAppts, error } = await historyQuery;

      if (error) throw error;

      if (historyAppts) {
        const currentAppts = this.appointmentsTable();
        const seenIds = new Set(currentAppts.map(a => a.id));
        const newAppts = historyAppts
          .filter(a => !seenIds.has(a.id))
          .map(a => this.mapDbToAppointment(a));

        if (newAppts.length > 0) {
          this.appointmentsTable.set([...currentAppts, ...newAppts]);
        }
        this.loadedMonths.add(key);
      }
    } catch (err) {
      console.error(`[DB] Error loading historical data for ${key}: `, err);
    }
  }


  readonly procedures = computed(() => {
    const allProcs = this.proceduresTable();
    const currentMunId = this.currentMunicipalityId();
    const muns = this.municipalitiesTable();

    if (!currentMunId) {
      return [];
    }

    const mun = muns.find(m => String(m.id) === String(currentMunId));

    // Filter by Municipality and by Active Categories
    const filtered = allProcs.filter(p => {
      const isMyMun = String(p.municipalityId) === String(currentMunId);
      if (!isMyMun) return false;

      const cat = p.category.toLowerCase();

      // Sempre permitir Cirurgias e Pré-Operatório
      if (cat.includes('cirurgia') || cat.includes('pré-operatório')) return true;

      // Toggleable categories
      if (cat.includes('consulta') && !mun?.enable_consultas) return false;
      if (cat.includes('exame') && !mun?.enable_exames) return false;
      if (cat.includes('oci') && !mun?.enable_oci) return false;

      // Remove deprecated categories entirely
      if (cat.includes('terapia') || cat.includes('outros')) return false;

      return true;
    });

    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  });
  readonly systemLogs = this.systemLogsTable.asReadonly();
  readonly locations = this.locationsTable.asReadonly(); // Public access

  // --- REATORADO: USA RPC PARA PERFORMANCE ---
  readonly dashboardStats = computed(() => {
    // Por enquanto, retorna os dados da RPC.
    // Se filtros específicos (Executor/Local) forem ativos, poderíamos aplicar um ajuste local aqui.
    return this.dashboardSummaryData();
  });

  // --- SHARED DASHBOARD LOGIC (Optimization) ---

  readonly uniqueCategories = computed(() => {
    const munId = this.currentMunicipalityId();
    const role = this.currentUserRole();
    const muns = this.municipalitiesTable();
    const targetMun = muns.find(m => String(m.id) === String(munId));

    // Categorias base que sempre devem existir se habilitadas
    const baseCategories: string[] = ['CIRURGIA', 'PRÉ-OPERATÓRIO'];
    if (targetMun?.enable_consultas) {
      baseCategories.push('CONSULTA BÁSICA');
      baseCategories.push('CONSULTA ESPECIALIZADA');
    }
    if (targetMun?.enable_exames) {
      baseCategories.push('EXAMES');
    }
    if (targetMun?.enable_oci) {
      baseCategories.push('OCI');
    }

    // Categorias vindas dos procedimentos atuais
    const existingCats = this.procedures().map(p => p.category);

    // União e normalização total para evitar duplicatas visuais
    const allUniqueRaw = [...baseCategories, ...existingCats];
    const categoryMap = new Map<string, string>(); // Normalized -> RawOriginal

    allUniqueRaw.forEach(c => {
      const norm = normalizeCategory(c);
      if (!categoryMap.has(norm) || (c.includes('Á') || c.includes('É'))) {
        // Preferencialmente manter a versão com acento se disponível
        categoryMap.set(norm, c.toUpperCase());
      }
    });

    const filtered = Array.from(categoryMap.values()).filter((cat) => {
      const normC = normalizeCategory(cat);

      // ACS restricted to CONSULTA BÁSICA
      if (role === 'ACS') return normC === 'CONSULTA BASICA';

      if (normC === "CONSULTA BASICA" || normC === "CONSULTA ESPECIALIZADA")
        return !!targetMun?.enable_consultas;

      if (normC === "EXAMES") return !!targetMun?.enable_exames;
      if (normC === "OCI") return !!targetMun?.enable_oci;

      // Remove categorias obsoletas se sobrarem no banco
      if (normC.includes('TERAPIA') || normC.includes('OUTROS')) return false;
      return true;
    });

    return [
      { label: 'Todas', value: null },
      ...filtered.map(cat => ({ label: cat, value: cat }))
    ];
  });

  readonly uniqueExecutors = computed(() => {
    const munId = this.dashboardMunicipalityFilter() || this.currentMunicipalityId();
    const filtered = munId
      ? this.executorsTable().filter(e => e.municipalityId === munId)
      : this.executorsTable();
    return [
      { label: 'Todos', value: null },
      ...filtered.map(e => ({ label: e.name, value: e.id }))
    ];
  });

  readonly uniqueLocations = computed(() => {
    const munId = this.dashboardMunicipalityFilter() || this.currentMunicipalityId();
    const filtered = munId
      ? this.locationsTable().filter(l => l.municipalityId === munId)
      : this.locationsTable();
    return [
      { label: 'Todos', value: null },
      ...filtered.map(l => ({ label: l.name, value: l.name }))
    ];
  });

  readonly uniqueAcs = computed(() => {
    const munId = this.dashboardMunicipalityFilter() || this.currentMunicipalityId();
    const filtered = munId
      ? this.acsTable().filter(a => a.municipalityId === munId)
      : this.acsTable();
    return [
      { label: 'Todos', value: null },
      ...filtered.map(a => ({ label: a.name, value: a.id }))
    ];
  });

  readonly filteredDashboardProcedures = computed(() => {
    const cat = this.dashboardCategoryFilter();
    let all = this.procedures();

    let procs = all;
    if (cat && cat !== 'Todas' && cat !== 'Todas as Categorias') {
      procs = all.filter(p => p.category === cat);
    }
    return [
      { label: 'Todos', value: null },
      ...procs.map(p => ({ label: p.name, value: p.id }))
    ];
  });

  /**
   * Helper function for drill-down filtering
   * Separation of concerns: Logic isolated for optimization
   */
  public filterDashboardPatients(
    patients: PatientProcess[],
    filters: {
      term: string,
      status: string | string[] | null,
      procId: number | null,
      cat: string | null,
      execId: string | null,
      loc: string | null,
      acsId: string | null,
      month: number,
      year: number,
      ignoreDateFilter?: boolean,
      excludeUbsCategories?: boolean
    }
  ): PatientProcess[] {
    return patients.filter(p => {
      const role = this.currentUserRole();

      // ACS restriction: Only see basic consultations
      if (role === 'ACS' && p.category !== 'CONSULTA BÁSICA') return false;

      let searchClean = filters.term;
      if (searchClean && /^[\d\.\-\s]+$/.test(searchClean) && searchClean.replace(/\D/g, '').length >= 3) {
        searchClean = searchClean.replace(/\D/g, '');
      }

      const cleanCycleCpf = p.cpf ? p.cpf.replace(/\D/g, '') : '';
      const matchTerm = !filters.term ||
        (p.name && p.name.toLowerCase().includes(filters.term)) ||
        (p.socialName && p.socialName.toLowerCase().includes(filters.term)) ||
        // Verifica CPF limpo contra termo limpo OU CPF original contra termo original
        cleanCycleCpf.includes(searchClean) ||
        (p.cpf && p.cpf.includes(filters.term));

      if (!matchTerm) return false;
      if (filters.procId && p.procedureId !== filters.procId) return false;
      if (!filters.procId && filters.cat && filters.cat !== 'Todas' && filters.cat !== 'Todas as Categorias' && p.category !== filters.cat) return false;

      if (filters.execId && filters.execId !== 'Todos') {
        const selectedExec = this.executorsTable().find(e => String(e.id) === String(filters.execId));
        const filterName = (selectedExec?.name || '').toLowerCase().trim();
        const eName = (p.executorName || '').toLowerCase().trim();
        const isIdMatch = String(p.executorId) === String(filters.execId);
        const isNameMatch = eName && filterName && (eName.includes(filterName) || filterName.includes(eName));
        if (!isIdMatch && !isNameMatch) return false;
      }

      if (filters.loc && filters.loc !== 'Todos') {
        const lf = filters.loc.toLowerCase().trim();
        const sLoc = (p.schedulingData?.location || '').toLowerCase().trim();
        const oLoc = (p.outcomeData?.location || '').toLowerCase().trim();
        if (!sLoc.includes(lf) && !oLoc.includes(lf)) return false;
      }

      if (filters.acsId && p.acsId !== filters.acsId) return false;

      let matchStatus = true;
      if (filters.status) {
        const statusList = Array.isArray(filters.status) ? filters.status : [filters.status];

        // Mapeamento de grupos de status para coincidir com a RPC
        let effectiveStatusList = [...statusList];
        if (statusList.includes('Cancelado') && !statusList.includes('Não Realizado')) {
          effectiveStatusList.push('Não Realizado');
        }

        matchStatus = effectiveStatusList.includes(p.status);

        if (matchStatus) {
          // Se ignoreDateFilter for true, pula a verificação de data local.
          // Atendimentos que não são estritamente de fila de espera exigem filtro de data (caso não seja ignorado)
          if (!filters.ignoreDateFilter && !effectiveStatusList.includes('Aguardando') && !effectiveStatusList.includes('Em Agendamento')) {
            let d: string | undefined;
            if (p.status === 'Realizado' || p.status === 'Não Realizado' || p.status === 'Cancelado') {
              d = p.outcomeData?.date || p.schedulingData?.date;
            } else {
              d = p.schedulingData?.date;
            }

            if (!d && p.registrationDate) {
              const rd = new Date(p.registrationDate);
              if (!isNaN(rd.valueOf())) {
                const m = String(rd.getMonth() + 1).padStart(2, '0');
                const y = String(rd.getFullYear());
                const day = String(rd.getDate()).padStart(2, '0');
                d = `${y}-${m}-${day}`;
              }
            }

            if (d) {
              const normalized = DateUtil.toBrasiliaDateString(d);
              if (normalized) {
                const [y, m] = normalized.split('-').map(Number);
                if ((m - 1) !== filters.month || y !== filters.year) matchStatus = false;
              } else {
                matchStatus = false;
              }
            } else {
              matchStatus = false;
            }
          }
        }
      }

      return matchStatus;
    });
  }

  readonly getFilteredDashboardPatients = computed(() => {
    const filters = {
      term: this.drillDownSearch().toLowerCase(),
      status: this.drillDownFilterStatus(),
      procId: this.dashboardProcedureFilter(),
      cat: this.dashboardCategoryFilter(),
      execId: this.dashboardExecutorFilter(),
      loc: this.dashboardLocationFilter(),
      acsId: this.drillDownAcsFilter(),
      month: this.dashboardDate().getMonth(),
      year: this.dashboardDate().getFullYear()
    };
    return this.filterDashboardPatients(this.allPatientsList(), filters);
  });

  readonly filteredDashboardData = this.getFilteredDashboardPatients;

  /**
   * Helper function for grouping quotas
   */
  public calculateQuotaGroups(pools: QuotaPool[], monthRef: string, municipality: Municipality | null) {
    const role = this.currentUserRole();
    const filteredPools = pools.filter(p => {
      const poolMonth = p.monthRef?.substring(0, 7);
      if (poolMonth !== monthRef) return false;

      const normCat = normalizeCategory(p.category);

      // UBS Module filtering
      const isMunReg = municipality?.enable_ubs_module && (role === 'OPERADOR' || role === 'OPERADOR_MUNICIPAL');
      if (isMunReg && normCat === 'CONSULTA BASICA') {
        return false;
      }
      if (role === 'REGULADOR_UBS') {
        if (normCat !== 'CONSULTA BASICA') return false;
      }

      if (normCat === 'CONSULTA BASICA' || normCat === 'CONSULTA ESPECIALIZADA') return !!municipality?.enable_consultas;
      if (normCat === 'EXAMES') return !!municipality?.enable_exames;
      return true;
    });

    const map = new Map<string, any[]>();
    filteredPools.forEach(pool => {
      // Usamos a versão bruta para o pool, mas a chave do Map será normalizada 
      // para agrupar variações de acentuação no mesmo título do dashboard.
      const rawCat = pool.category || 'Outros';
      const catKey = normalizeCategory(rawCat);

      if (!map.has(catKey)) map.set(catKey, []);
      map.get(catKey)?.push(pool);
    });

    const groups: { category: string; pools: any[] }[] = [];
    map.forEach((list, catKey) => {
      // Usamos a categoria do primeiro item do grupo como label visual
      const displayLabel = list[0]?.category || catKey;
      groups.push({ category: displayLabel, pools: list });
    });

    return groups.sort((a, b) => a.category.localeCompare(b.category));
  }

  readonly quotaGroups = computed(() => {
    const date = this.dashboardDate();
    const monthRef = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const mun = this.municipalitiesTable().find(m => String(m.id) === String(this.currentMunicipalityId())) || null;
    return this.calculateQuotaGroups(this.quotaPoolsTable(), monthRef, mun);
  });

  public openDrillDown(type: 'waiting' | 'scheduled' | 'done' | 'canceled' | 'patients' | 'appointments') {
    // Reset filters
    this.drillDownType.set(type);
    this.drillDownSearch.set('');
    this.drillDownAcsFilter.set(null);
    this.dashboardProcedureFilter.set(null);
    this.dashboardCategoryFilter.set(null);
    this.dashboardExecutorFilter.set(null);
    this.drillDownFilterStatus.set(null);

    switch (type) {
      case 'waiting':
        this.drillDownTitle.set('Pacientes Aguardando Vaga');
        this.drillDownFilterStatus.set(['Aguardando', 'Em Agendamento']);
        break;
      case 'scheduled':
        this.drillDownTitle.set('Agendamentos Confirmados');
        this.drillDownFilterStatus.set('Agendado');
        break;
      case 'done':
        this.drillDownTitle.set('Procedimentos Realizados');
        this.drillDownFilterStatus.set('Realizado');
        break;
      case 'canceled':
        this.drillDownTitle.set('Cancelados / Ausentes');
        this.drillDownFilterStatus.set(['Não Realizado', 'Cancelado']);
        break;
      case 'patients':
        this.drillDownTitle.set('Base Completa de Pacientes');
        this.drillDownFilterStatus.set(null);
        break;
      case 'appointments':
        this.drillDownTitle.set('Solicitações');
        this.drillDownFilterStatus.set(['Aguardando', 'Em Agendamento', 'Agendado', 'Realizado', 'Não Realizado', 'Cancelado']);
        break;
    }

    this.dashboardView.set('PATIENTS_FULL');
  }

  public onDashboardCategoryChange(catValue: any) {
    this.dashboardCategoryFilter.set(catValue);
    this.dashboardProcedureFilter.set(null); // Reset procedure filter when category changes
  }

  public updateProcedureFilter(procId: any) {
    this.dashboardProcedureFilter.set(procId);
  }

  readonly pendingRealizations = computed(() => this.apptService.pendingRealizations());
  readonly pendingRealizationsCount = computed(() => {
    const summary = this.dashboardSummaryData();
    if (summary && summary.pending_realizations !== undefined && summary.pending_realizations !== null && summary.pending_realizations > 0) {
      return Number(summary.pending_realizations);
    }
    return this.pendingRealizationsCountState();
  });



  // Methods moved to components for better lazy loading of DownloadService

  private getExportMetadata(prefix: string) {
    const now = new Date();
    return {
      protocol: `EXP - ${prefix} -${now.getTime()} `,
      date: this.datePipe.transform(now, 'dd/MM/yyyy') || '',
      time: this.datePipe.transform(now, 'HH:mm:ss') || '',
      user: this.currentOperator()?.name || 'SISTEMA'
    };
  }

  // Helper for drilldown mapping if needed elsewhere
  public mapDbToPatientProcess(dbAppt: any): PatientProcess {
    const patientMap = new Map(this.patientsTable().map(p => [p.id, p]));
    const procMap = new Map(this.procedures().map(p => [p.id, p]));

    const patient = patientMap.get(dbAppt.patient_id);
    const procedure = procMap.get(dbAppt.procedure_id);

    const mappedAppt = this.mapDbToAppointment(dbAppt);
    const acsInfo = patient?.acsId ? this.acsTable().find(ac => ac.id === patient.acsId) : null;

    const safePatient = patient || {
      id: 0, cpf: '', name: 'Paciente Não Encontrado', susCard: '---',
      birthDate: '', phone: '',
      address: { zip: '', street: '', number: '', district: '', city: '', uf: '' },
      municipalityId: '', registrationDate: new Date()
    } as any;

    return {
      ...safePatient,
      ...mappedAppt,
      procedureName: procedure?.name || 'Procedimento Desconhecido',
      category: (procedure?.category || 'OUTROS').toUpperCase(),
      acsName: acsInfo ? acsInfo.name : undefined,
      acsContact: acsInfo ? acsInfo.contactNumber : undefined,
      daysSinceStatusChange: this.calculateBriefDays(mappedAppt)
    } as PatientProcess;
  }

  calculateBriefDays(app: Appointment | PatientProcess): number {
    // Usar registrationDate como fallback se a propriedade específica de troca de status não existir no modelo
    const start = (app as any).lastStatusChange ? new Date((app as any).lastStatusChange) : new Date(app.registrationDate);

    // Usar o "agora" de Brasília para cálculo de dias
    const now = DateUtil.getBrasiliaNow();
    const diffTime = now.getTime() - start.getTime();
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  }






  readonly allPatientsList = computed(() => this.apptService.allPatientsList());
  readonly waitingList = computed(() => this.apptService.allPatientsList());




  readonly quotaStats = computed(() => {
    const pools = this.quotaPoolsTable();
    const statsData = this.quotaStatsData();
    const selectedDate = this.dashboardDate();

    // If we have accurate stats from the database RPC, use those
    if (statsData.length > 0) {
      return statsData.map(stat => ({
        id: stat.procedure_id,
        name: stat.procedure_name,
        category: stat.category,
        limit: stat.total_authorized,
        booked: stat.booked_count,
        waiting: stat.waiting_count,
        waitingReady: stat.waiting_ready_count,
        available: stat.available_count,
        percent: stat.total_authorized > 0 ? (stat.booked_count / stat.total_authorized) * 100 : 0,
        isImaging: stat.is_imaging
      }));
    }

    // Fallback: Apenas retorna os limites sem iteração local profunda para evitar travamentos
    // Os contadores serão preenchidos nativamente pela RPC 'get_dashboard_summary'
    return pools.map(pool => {
      // Importante: count_booked e afins serão garantidos pela estatística transacional no servidor,
      // esse fallback focado não estressa a memória do Angular
      return {
        id: pool.procedureId,
        name: pool.procedureName || '',
        category: pool.category || '',
        limit: pool.totalAuthorized,
        booked: pool.bookedCount || 0,
        waiting: 0,
        waitingReady: 0,
        available: Math.max(0, (pool.distributedCount || 0) - (pool.bookedCount || 0)),
        percent: pool.totalAuthorized > 0 ? ((pool.bookedCount || 0) / pool.totalAuthorized) * 100 : 0,
        isImaging: false
      };
    });
  });

  /**
   * Propriedade computada otimizada para verificar se existe pelo menos
   * uma vaga a ser distribuída (vaga disponível + paciente aguardando).
   * Retorna true se encontrar qualquer procedimento que atenda aos critérios.
   */
  readonly hasAvailableVacancies = computed(() => {
    return this.quotaStats().some(q => q.available > 0 && q.waiting > 0);
  });

  isAnalyzingVacancies = signal(false);
  promotableVacanciesExist = signal(false);

  /**
   * Versão V7 do analyzePromotableVacancies
   * Considera Procedimentos Sob Demanda (UBS) vs Tradicionais (Pool)
   */
  async analyzePromotableVacancies(monthRef?: string) {
    if (this.isAnalyzingVacancies()) return;

    const munId = this.currentMunicipalityId();
    if (!munId) return;

    this.isAnalyzingVacancies.set(true);

    try {
      const operator = this.currentOperator();
      const role = this.currentUserRole();
      const locationId = (role === 'REGULADOR_UBS' || role === 'OPERADOR') ? operator?.location_id : null;

      const date = this.dashboardDate();
      const targetMonth = monthRef || `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      // Logica V7: 
      // Se for Regulador UBS, o banco vai checar professional_team_quotas filtrando por location_id
      // Se for Operador Municipal, o banco vai checar quota_pools excluindo categorias de UBS se habilitado

      const excludeUbsCategories = !!(this.municipalitiesTable().find(m => m.id === munId)?.enable_ubs_module &&
        (role === 'OPERADOR' || role === 'OPERADOR_MUNICIPAL'));

      const { data, error } = await supabase.rpc('check_promotable_vacancies', {
        p_municipality_id: munId,
        p_month_ref: targetMonth,
        p_location_id: locationId,
        p_exclude_ubs_categories: excludeUbsCategories
      });

      if (error) throw error;
      this.promotableVacanciesExist.set(!!data);

    } catch (err) {
      console.error('[DB] Error analyzing vacancies V7:', err);
      this.promotableVacanciesExist.set(false);
    } finally {
      this.isAnalyzingVacancies.set(false);
    }
  }

  /**
   * Obtém relatório consolidado de números absolutos de pacientes por procedimento e status.
   */
  async getProcedureStatusReport(municipalityId: string | null, year?: number, month?: number, excludeUbsCategories: boolean = false) {
    return this.apptService.getProcedureStatusReport(municipalityId, year, month, excludeUbsCategories);
  }

  /**
   * Método centralizador para sincronizar o estado de contagem e vagas
   * após qualquer alteração (exclusão, agendamento, mudança de status).
   */
  async syncBusinessState(municipalityId: string | null = null) {
    const mId = municipalityId || this.currentMunicipalityId();
    if (!mId) return;

    const date = this.dashboardDate();
    const monthRef = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

    console.log(`[DB] V7 Syncing state for municipality ${mId} and month ${monthRef}`);

    // Context determination for stats
    const role = this.currentUserRole();
    const mun = this.municipalitiesTable().find(m => m.id === mId);
    const excludeUbsCategories = !!(mun?.enable_ubs_module && (role === 'OPERADOR' || role === 'OPERADOR_MUNICIPAL'));

    // Executa as principais sincronizações em paralelo para máxima eficiência
    await Promise.all([
      // 1. Estatísticas do Dashboard (Cards do topo) - V7 Context Aware
      this.refreshDashboardStats(mId, date.getFullYear(), date.getMonth() + 1, { excludeUbsCategories }),

      // 2. Estatísticas de Cotas (Barra lateral e badges)
      this.quotaService.loadQuotaStats(mId, monthRef),

      // 3. Pools de Cotas (Tabela de cotas)
      this.quotaService.refreshQuotaPools(mId, monthRef),

      // 4. Verificação de vagas para redistribuição (V7 Analysis)
      this.analyzePromotableVacancies(monthRef)
    ]);

    // 5. Dispara trigger para forçar re-busca de listas paginadas (Fila, Agendados, etc)
    this.queueRefreshTrigger.update(v => v + 1);
  }

  /**
   * Verifica se há vagas E pacientes válidos para promoção (ignorando pacientes
   * de pré-operatório que ainda não concluíram todas as etapas).
   */
  readonly hasPromotableVacancies = computed(() => {
    return this.promotableVacanciesExist();
  });


  /**
   * @deprecated Use initContext + loadBusinessData pattern in ContextService
   */
  async initDatabase(municipalityId: string | null = null) {
    return this.loadingService.withLoading(async () => {
      await this.initContext(municipalityId);
      await this.loadBusinessData(municipalityId);
    });
  }

  /**
   * STEP 1: Fast Initialization
   * Loads only configuration data needed for the UI shell.
   * Clears previous business data to avoid stale state.
   */
  async initContext(municipalityId: string | null = null) {
    this.currentMunicipalityId.set(municipalityId);

    // CLEAR BUSINESS DATA (Important for "Empty State" while loading)
    this.patientsTable.set([]);
    this.appointmentsTable.set([]);
    this.systemLogsTable.set([]);

    try {
      // 1. Initial Load of Essential Context
      await Promise.all([
        this.municipalityService.refreshMunicipalities(),
        this.quotaService.refreshProcedures(municipalityId),
        this.quotaService.refreshLocations(municipalityId),
        this.quotaService.refreshExecutors(municipalityId),
        this.acsService.refreshAcs(municipalityId),
        this.acsService.refreshAcsTeams(municipalityId),
        this.profService.refreshProfessionals(municipalityId),
        this.operatorService.refreshOperators()
      ]);
    } catch (err) {
      console.error('[DB] Critical Context Load Error:', err);
      throw err; // Blocking error
    }
  }

  /**
   * STEP 2: Heavy Business Data Load
   * Loads Patients, Appointments, Logs.
   * Updates loadingService to drive UI skeletons.
   */
  async loadBusinessData(municipalityId: string | null = null) {
    try {
      // 1. Business Data Load (Parallel in background - non-blocking)
      const date = this.dashboardDate();
      const monthRef = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      await Promise.all([
        this.refreshPatientData(municipalityId),
        municipalityId ? this.quotaService.refreshQuotaPools(municipalityId, monthRef) : Promise.resolve(),
        municipalityId ? this.loadQuotaStats(municipalityId, monthRef) : Promise.resolve()
      ]);
    } catch (err) {
      console.error('[DB] Business Data Load Error:', err);
    }
  }

  /**
   * Delega o carregamento de quota stats ao QuotaService.
   * Ambos os services agora compartilham o mesmo signal `quotaStatsData`.
   */
  async loadQuotaStats(municipalityId: string, monthRef?: string) {
    if (!municipalityId) return;
    // Usa o mês selecionado no painel se não for passado
    const targetMonth = monthRef || (() => {
      const d = this.procedureMonth();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    })();

    // Resolver contexto de filtros automáticos
    const role = this.currentUserRole();
    const operator = this.currentOperator();
    const mun = this.municipalitiesTable().find(m => m.id === municipalityId);
    const hasUbs = mun?.enable_ubs_module === true;

    // Regra: Regulador Municipal não vê consultas básicas se módulo UBS ativo
    const excludeUbs = hasUbs && (role === 'OPERADOR' || role === 'OPERADOR_MUNICIPAL' || role === 'ADMIN_MUNICIPAL');

    // Regra: Regulador de UBS filtra pela sua própria unidade
    const locId = (role === 'REGULADOR_UBS' || role === 'OPERADOR') ? operator?.location_id : null;

    return this.quotaService.loadQuotaStats(municipalityId, targetMonth, locId, excludeUbs);
  }

  /**
   * LIGHTWEIGHT REFRESH: Atualiza a contagem de pacientes cadastrados
   * de forma pontual (head count), sem download massivo de dados para a memória.
   */
  async refreshPatientData(municipalityId: string | null) {
    if (!municipalityId && this.currentOperator()?.role !== 'ADMIN_GERAL') return;

    try {
      this.refreshPendingRealizationsCount(municipalityId);
      if (municipalityId) {
        const { count } = await supabase
          .from('patients')
          .select('*', { count: 'exact', head: true })
          .eq('municipality_id', municipalityId);
        if (count !== null) this.patientService.totalPatients.set(count);
      } else if (this.currentOperator()?.role === 'ADMIN_GERAL') {
        const { count } = await supabase
          .from('patients')
          .select('*', { count: 'exact', head: true });
        if (count !== null) this.patientService.totalPatients.set(count);
      }
    } catch (err) {
      console.warn('[DB] refreshPatientData count error:', err);
    }
  }


  // Re-map internal private logAction to use new service
  private async logAction(action: string, target: string, details: any, user?: string, preDefinedProtocol?: string) {
    const protocol = await this.auditService.logAction(action, target, details, user, preDefinedProtocol);

    const newLog: SystemLog = {
      id: crypto.randomUUID(),
      protocol,
      timestamp: new Date(),
      action,
      target,
      user: user || this.currentOperator()?.name || 'Sistema',
      municipalityId: this.currentMunicipalityId() || 'NÃO IDENTIFICADO',
      details
    };
    await this.updateEntityAuditTrail(newLog, details);

    return protocol;
  }

  private async updateEntityAuditTrail(log: SystemLog, details: any) {
    if (!details) return;

    let table = '';
    const entityId = details.appointmentId || details.patientId || details.procedureId;

    if (details.appointmentId) table = 'appointments';
    else if (details.patientId) table = 'patients';
    else if (details.procedureId) table = 'procedures';

    if (!table || !entityId) return;

    try {
      const { data, error } = await supabase.from(table).select('audit_trail').eq('id', entityId).maybeSingle();
      if (error) throw error;

      const currentTrail = Array.isArray(data?.audit_trail) ? data.audit_trail : [];
      const updatedTrail = [log, ...currentTrail].slice(0, 50);

      await supabase.from(table).update({ audit_trail: updatedTrail }).eq('id', entityId);
    } catch (err) {
      console.error(`[DatabaseService] Erro ao atualizar trilha em ${table}: `, err);
    }
  }

  async recordExportLog(type: string, target: string, protocol: string) {
    return this.auditService.recordExportLog(type, target, protocol);
  }


  async gerarProtocolo(
    tipoDocumento: TipoDocumento,
    entidadeTipo: 'PACIENTE' | 'PROCEDIMENTO' | 'AGENDAMENTO',
    entidadeId: number,
    operadorId: number,
    observacoes?: string,
    municipioId?: string | null
  ): Promise<string> {
    return this.auditService.gerarProtocolo(tipoDocumento, entidadeTipo, entidadeId, operadorId, undefined, undefined, observacoes, municipioId);
  }

  async registrarAuditoria(
    protocoloReferencia: string,
    tipoAcao: TipoAcao,
    descricaoAcao: string,
    operadorId: number,
    arg5?: any,
    arg6?: any,
    arg7?: any
  ): Promise<void> {
    await this.auditService.registrarAuditoria(protocoloReferencia, tipoAcao, descricaoAcao, operadorId, arg5, arg6, arg7);
  }

  async checkDuplicateProcedure(
    patientId: number | null,
    cpf: string,
    procedureId: number,
    dateToCheck: string, // YYYY-MM-DD
    excludeApptId?: number
  ): Promise<{ exists: boolean, message?: string }> {
    return this.apptService.checkDuplicateProcedure(patientId, cpf, procedureId, dateToCheck, excludeApptId);
  }

  async getPendingRealizationsForPatient(
    patientId: number | null,
    cpf?: string
  ): Promise<any[]> {
    return this.apptService.getPendingRealizationsForPatient(patientId, cpf);
  }

  async resolvePendingRealization(
    appointmentId: number,
    outcome: 'Realizado' | 'Aguardando' | 'Cancelado',
    operatorId: number,
    outcomeLocation?: string
  ): Promise<Appointment> {
    return this.apptService.resolvePendingRealization(appointmentId, outcome, operatorId, outcomeLocation);
  }

  // --- ACTIONS WITH LOGGING (RETURNING PROTOCOL) ---

  async addPatient(
    patientData: Omit<Patient, 'id' | 'registrationDate'> & { registrationDate?: any },
    appointmentData: any,
    operatorId: number
  ) {
    return this.patientService.createPatientWithAppointment(patientData, appointmentData, operatorId);
  }



  // --- OPTIMIZATION METHODS ---

  /**
   * Fetches a single patient with ALL data (including images).
   * Used for on-demand loading in PatientDetailModal.
   */
  async getPatientById(id: number): Promise<Patient | null> {
    return this.patientService.getPatientById(id);
  }


  async fetchPatientsPaginated(limit: number, offset: number, searchTerm?: string) {
    const munId = this.currentMunicipalityId();
    return this.patientService.fetchPatientsPaginated(munId, limit, offset, searchTerm);
  }

  async fetchAppointmentsPaginated(statusGroup: string[], limit: number, offset: number) {
    const munId = this.currentMunicipalityId();
    return this.apptService.fetchAppointmentsPaginated(munId, statusGroup, limit, offset);
  }

  async getAppointmentById(id: number): Promise<Appointment | null> {
    return this.apptService.getAppointmentById(id);
  }

  async getAppointmentsByParentId(parentId: number): Promise<Appointment[]> {
    return this.apptService.getAppointmentsByParentId(parentId);
  }

/**
    * Lazy loads system logs. Call this only when opening the Audit tab.
    */
  async loadSystemLogs(limit = 100, offset = 0, municipalityId?: string) {
    this.loadingService.set(true);
    try {
      return await this.auditService.loadSystemLogs(limit, offset, municipalityId);
    } finally {
      this.loadingService.set(false);
    }
  }

  // --- CIRURGIA E ETAPAS ---

  async fetchSurgicalCycles(municipalityId: string, opts?: { search?: string; procedureId?: number | null; month?: string; }) {
    return this.apptService.fetchSurgicalCycles(municipalityId, opts);
  }

  async getSurgerySteps(procedureId: number): Promise<SurgeryStepConfig[]> {
    return this.apptService.getSurgerySteps(procedureId);
  }

  async updateSurgerySteps(procedureId: number, steps: Omit<SurgeryStepConfig, 'id'>[]): Promise<void> {
    return this.apptService.updateSurgerySteps(procedureId, steps);
  }

  async createNextSurgeryStep(parentAppointmentId: number, nextStepIndex: number, operatorId: number): Promise<string | null> {
    return this.apptService.createNextSurgeryStep(parentAppointmentId, nextStepIndex, operatorId);
  }

  async closeSurgicalCycle(parentAppointmentId: number, operatorId: number): Promise<void> {
    return this.apptService.closeSurgicalCycle(parentAppointmentId, operatorId);
  }

  async restartSurgicalCycle(parentAppointmentId: number, operatorId: number): Promise<void> {
    return this.apptService.restartSurgicalCycle(parentAppointmentId, operatorId);
  }

  async skipSurgeryStep(stepAppointmentId: number, operatorId: number): Promise<void> {
    return this.apptService.skipSurgeryStep(stepAppointmentId, operatorId);
  }

  async completeSurgeryStep(stepAppointmentId: number, operatorId: number, outcomeDate?: string, outcomeLocation?: string): Promise<void> {
    return this.apptService.completeSurgeryStep(stepAppointmentId, operatorId, outcomeDate, outcomeLocation);
  }



  async deleteAppointment(id: number, operatorId: number) {
    return this.apptService.deleteAppointmentWithLogic(id, operatorId);
  }

  async deletePatient(id: number, operatorId: number) {
    return this.patientService.deletePatient(id, operatorId);
  }

  async addProcedure(proc: Omit<Procedure, 'id' | 'latestProtocol' | 'protocolHistory'>, operatorId: number, targetMunicipalityId?: string) {
    return this.quotaService.addProcedure(proc, operatorId, targetMunicipalityId);
  }

  async updateProcedure(updated: Procedure) {
    const opId = this.currentOperator()?.id || 0;
    return this.quotaService.updateProcedure(updated, opId);
  }




  async updatePatient(patient: Patient, operatorId: number, protocoloReferencia?: string) {
    return this.patientService.updatePatient(patient, operatorId, protocoloReferencia);
  }

  async findPatientByCpf(cpf: string, birthDate?: string) {
    const patient = await this.patientService.findPatientByCpf(cpf, birthDate);
    if (!patient) return null;

    const list = this.waitingList();
    const processes = list.filter(process => process.patientId === patient.id);

    return {
      patient,
      processes,
      history: await this.getPatientHistory(patient.id)
    };
  }

  findLogByProtocol(protocol: string) {
    return this.auditService.findLogByProtocol(protocol);
  }

  async findRelatedLogs(referenceLog: SystemLog, municipalityId?: string | null): Promise<any[]> {
    return this.auditService.findRelatedLogs(referenceLog, municipalityId);
  }

  async findFiscalProtocolAsync(protocol: string, municipalityId?: string | null): Promise<SystemLog | null> {
    return this.auditService.findFiscalProtocolAsync(protocol, municipalityId);
  }

  /**
   * Busca o histórico de auditoria relacionado a um protocolo.
   */
  async findFiscalHistoryAsync(referenceLog: SystemLog, municipalityId?: string | null): Promise<SystemLog[]> {
    return this.auditService.findFiscalHistoryAsync(referenceLog, municipalityId);
  }

  async getUserActivity(identifier: string, operatorId?: number, municipalityId?: string | null): Promise<SystemLog[]> {
    return this.auditService.getUserActivity(identifier, operatorId, municipalityId);
  }

  async fetchAuditLogs(month: string, municipalityId: string | null): Promise<SystemLog[]> {
    return this.auditService.fetchAuditLogs(month, municipalityId);
  }

  // --- URGENCY, FORECAST & DISTRIBUTION (Delegated to QuotaService) ---

  async markAsUrgency(appointmentId: number, justification?: any, operatorId?: number) {
    return this.quotaService.markAsUrgency(appointmentId, justification, operatorId);
  }

  calculatePatientForecast(app: Appointment | PatientProcess, avgWaitDays?: number | null): string {
    return this.quotaService.calculatePatientForecast(app, avgWaitDays);
  }

  async getPatientQueuePosition(appointmentId: number, procedureId: number, municipalityId: string): Promise<number | null> {
    return this.quotaService.getPatientQueuePosition(appointmentId, procedureId, municipalityId);
  }

  async getPatientForecastPosition(appointmentId: number, procedureId: number, municipalityId: string): Promise<number | null> {
    return this.quotaService.getPatientForecastPosition(appointmentId, procedureId, municipalityId);
  }

  async getAverageMonthlyUsage(procedureId: number, municipalityId: string): Promise<number | null> {
    return this.quotaService.getAverageMonthlyUsage(procedureId, municipalityId);
  }

  async getMonthlyCapacity(procedureId: number, municipalityId: string): Promise<number | null> {
    return this.quotaService.getMonthlyCapacity(procedureId, municipalityId);
  }


  async getAverageWaitTimeDays(procedureId: number | null, municipalityId: string): Promise<number | null> {
    return this.quotaService.getAverageWaitTimeDays(procedureId, municipalityId);
  }

  async getAllProceduresWaitTimes(municipalityId: string): Promise<{procedureId: number, procedureName: string, avgDays: number | null}[]> {
    return this.quotaService.getAllProceduresWaitTimes(municipalityId);
  }

  checkQuotaAvailability(procedureId: number): boolean {
    return this.quotaService.checkQuotaAvailability(procedureId);
  }

  async distributeVacancies(onProgress?: (progress: DistributeProgressInfo) => void): Promise<{ count: number, breakdown?: Record<string, number> }> {
    const munId = this.currentMunicipalityId();
    if (!munId) return { count: 0, breakdown: {} };

    // Usa o mês selecionado no dashboard para a distribuição
    const date = this.dashboardDate();
    const monthRef = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

    return this.quotaService.distributeVacancies(munId, monthRef, onProgress);
  }

  // --- OPERATOR MANAGEMENT ---

  async refreshOperators(municipalityId?: string) {
    return this.operatorService.refreshOperators(municipalityId);
  }

  async addOperator(name: string, municipalityId: string, login: string, password: string, role: string = 'OPERADOR', locationId?: number) {
    return this.operatorService.createOperator(name, municipalityId, login, password, role, locationId || null);
  }

  // Procedimentos migrados para OperatorService
  async createOperator(n: string, m: string, l: string, p: string, r: string = 'OPERADOR_MUNICIPAL', loc: number | null = null, a?: string) {
    return this.operatorService.createOperator(n, m, l, p, r, loc, a);
  }

  async updateOperatorStatus(id: number, isActive: boolean) {
    return this.operatorService.updateOperatorStatus(id, isActive);
  }

  // Procedimentos migrados para MunicipalityService
  async getMunicipalities() {
    return this.municipalityService.getMunicipalities();
  }

  // Procedimentos migrados para OperatorService
  async authenticateOperator(login: string, pass: string) {
    return this.operatorService.authenticateOperator(login, pass);
  }

  async deleteOperator(id: number) {
    return this.operatorService.deleteOperator(id);
  }

  // --- GLOBAL SEARCH ---

  async globalSearchPatients(cpf: string, birthDate?: string) {
    if (!cpf || cpf.length < 11) return [];

    // Tenta primeiro com o CPF original (provavelmente formatado)
    let body = {
      p_cpf: cpf,
      p_birth_date: birthDate || null
    };

    let { data, error } = await supabase.rpc('search_patients_global', body);

    // Se falhar ou vier vazio, tenta com o CPF puramente numerico
    if ((!data || data.length === 0) && !error) {
      const cleanCpf = cpf.replace(/\D/g, '');
      if (cleanCpf !== cpf) {
        const retryResult = await supabase.rpc('search_patients_global', {
          p_cpf: cleanCpf,
          p_birth_date: birthDate || null
        });
        data = retryResult.data;
        error = retryResult.error;
      }
    }

    if (error) {
      console.error('[DB] Erro na pesquisa global:', error);
      throw error;
    }

    return (data || []).map(p => this.patientService.mapDbToPatient(p));
  }

  async getPublicAppointments(cpf: string, birthDate: string) {
    if (!cpf || cpf.length < 11 || !birthDate) return [];

    const { data, error } = await supabase.rpc('search_appointments_global', {
      p_cpf: cpf,
      p_birth_date: birthDate
    });

    if (error) {
      console.error('[DB] Erro ao buscar agendamentos publicamente:', error);
      throw error;
    }

    const patients = await this.globalSearchPatients(cpf, birthDate);
    if (!patients || patients.length === 0) return [];

    // Create a map of municipality_id -> patient for quick lookup
    const patientByMun = new Map(patients.map(p => [p.municipalityId, p]));

    const apps = (data || []).map(a => this.apptService.mapDbToAppointment(a));

    // Convert to PatientProcess format
    const mappedProcesses = await Promise.all(apps.map(async a => {
      const proc = this.proceduresTable().find(p => p.id === a.procedureId);

      let parentProcedureName: string | undefined;
      if (a.parentAppointmentId) {
        // Cidadão talvez não tenha o appt pai no payload se não foi retornado,
        // mas a RPC search_appointments_global retorna TODOS os agendamentos do paciente.
        const parentApp = apps.find(pa => pa.id === a.parentAppointmentId);
        if (parentApp) {
          const pProc = this.proceduresTable().find(p => p.id === parentApp.procedureId);
          parentProcedureName = pProc?.name;
        }
      }

      // Get the right patient based on appointment's municipality
      const appointmentPatient = patientByMun.get(a.municipalityId) || patients[0];
      const safePatient = {
        id: appointmentPatient.id, cpf: appointmentPatient.cpf, 
        name: appointmentPatient.socialName || appointmentPatient.name, 
        socialName: appointmentPatient.socialName,
        susCard: appointmentPatient.susCard || '---',
        birthDate: appointmentPatient.birthDate, phone: appointmentPatient.phone || '',
        address: appointmentPatient.address || { zip: '', street: '', number: '', district: '', city: '', uf: '' },
        municipalityId: a.municipalityId, registrationDate: appointmentPatient.registrationDate
      };

      // Injetar posição exata via RPC do banco calculada para o requerente
      let position = 0;
      if (a.status === 'Aguardando' || a.status === 'Em Agendamento') {
        try {
          const { data: posData } = await supabase.rpc('get_appointment_queue_position', {
            p_appointment_id: a.id
          });
          position = posData || 0;
        } catch (e) {
          console.error('Falha ao obter posição:', e);
        }
      }

      const exeName = (a as any).f_executor_name || (a as any).executorName || '';
      const munName = (a as any).f_municipality_name || '';
      const procName = (a as any).f_procedure_name;
      
      return {
        ...safePatient,
        ...a,
        procedureName: procName || proc?.name || 'Procedimento Desconhecido',
        executorName: exeName,
        municipalityName: munName,
        category: proc?.category || 'OUTROS',
        parentProcedureName,
        position
      };
    }));

    return mappedProcesses;
  }

  // --- PUBLIC ACCESSORS ---

  async getPatientHistory(patientId: number) {
    return this.patientService.getPatientHistory(patientId);
  }

  async addAppointmentToExistingPatient(
    patientId: number,
    appointmentData: Omit<Appointment, 'id' | 'patientId' | 'registrationDate' | 'status' | 'latestProtocol' | 'protocolHistory'>,
    operatorId: number
  ) {
    return this.patientService.addAppointmentToExistingPatient(patientId, appointmentData, operatorId);
  }

  async addAcs(acs: any) {
    return this.acsService.addAcs(acs);
  }

  async addAcsWithOperator(acs: any, login: string, pass: string) {
    return this.acsService.addAcsWithOperator(acs, login, pass);
  }

  async updateAcs(id: string, updates: any) {
    return this.acsService.updateAcs(id, updates);
  }

  async updateAcsWithOperator(id: string, updates: any, operatorId?: number, login?: string, pass?: string) {
    return this.acsService.updateAcsWithOperator(id, updates, operatorId, login, pass);
  }

  async deleteAcs(id: string) {
    return this.acsService.deleteAcs(id);
  }

  async refreshAcs(municipalityId: string) {
    return this.acsService.refreshAcs(municipalityId);
  }

  async addAcsTeam(team: any) {
    return this.acsService.addAcsTeam(team);
  }

  async updateAcsTeam(id: string, updates: any) {
    return this.acsService.updateAcsTeam(id, updates);
  }

  async deleteAcsTeam(id: string) {
    return this.acsService.deleteAcsTeam(id);
  }

  async refreshAcsTeams(municipalityId: string) {
    return this.acsService.refreshAcsTeams(municipalityId);
  }

  // --- PROFESSIONAL METHODS (MULTI-LOCATION) ---

  async refreshProfessionals(municipalityId: string) {
    return this.profService.refreshProfessionals(municipalityId);
  }

  async addProfessional(prof: any) {
    return this.profService.addProfessional(prof);
  }

  async updateProfessional(id: string, updates: any) {
    return this.profService.updateProfessional(id, updates);
  }

  async deleteProfessional(id: string) {
    return this.profService.deleteProfessional(id);
  }

  async getTeamProfessionals(teamId: string) {
    return this.profService.getTeamProfessionals(teamId);
  }

  async updateTeamProfessionals(teamId: string, professionalIds: string[]) {
    return this.profService.updateTeamProfessionals(teamId, professionalIds);
  }

  async refreshExecutors(municipalityId: string) {
    return this.quotaService.refreshExecutors(municipalityId);
  }

  async refreshLocations(municipalityId: string) {
    return this.quotaService.refreshLocations(municipalityId);
  }

  async updateProcedureLocation(updates: any) {
    const id = updates.id;
    const old = this.locationsTable().find(l => l.id === id);
    return this.quotaService.updateLocation(id, updates, old?.name);
  }

  // --- EXAM GROUPS (Agrupamentos) ---

  async getExamGroupMembers(procedureId: number) {
    return this.quotaService.getExamGroupMembers(procedureId);
  }

  async updateExamGroupMembers(procedureId: number, members: Omit<import('../models/procedure.model').ExamGroupMember, 'id' | 'parentProcedureId'>[]) {
    return this.quotaService.updateExamGroupMembers(procedureId, members);
  }

  async getImagingLocations(procedureId: number) {
    return this.quotaService.getImagingLocations(procedureId);
  }

  async updateImagingLocations(procedureId: number, locations: string[]) {
    return this.quotaService.updateImagingLocations(procedureId, locations);
  }

  async updatePassword(currentPass: string, newPass: string, operatorId: number) {
    return this.operatorService.updatePassword(currentPass, newPass, operatorId);
  }

  async searchPatientPublic(cpf: string, birthDate: string) {
    return this.patientService.findPatientByCpf(cpf, birthDate);
  }


  async addMunicipality(id: string, name: string, logo_url?: string, extras?: any) {
    return this.municipalityService.addMunicipality(id, name, logo_url, extras);
  }

  async updateMunicipality(id: string, updates: any) {
    return this.municipalityService.updateMunicipality(id, updates);
  }

  async updateMunicipalityStatus(id: string, isActive: boolean) {
    return this.municipalityService.updateMunicipalityStatus(id, isActive);
  }

  async deleteMunicipality(id: string) {
    return this.municipalityService.deleteMunicipality(id);
  }

  async getMunicipalityIpMask(municipalityId: string) {
    return this.municipalityService.getMunicipalityIpMask(municipalityId);
  }

  async saveMunicipalityIpMask(municipalityId: string, mask: any) {
    return this.municipalityService.saveMunicipalityIpMask(municipalityId, mask);
  }

  async clearAllPatients(municipalityId: string) {
    if (!municipalityId) throw new Error("Município inválido");

    // 1. Reset agenda slots (free them)
    const { error: slotError } = await supabase
      .from('agenda_slots')
      .update({
        patient_id: null,
        appointment_id: null,
        is_available: true,
        start_time: null,
        end_time: null,
        location_name: null,
        professional_name: null
      })
      .eq('municipality_id', municipalityId);

    if (slotError) console.error('[DB] Erro ao liberar vagas:', slotError);

    // 2. IMPORTANTE: Zera booked_count dos pools desta municipalidade
    // Isso garante que após limpeza de dados de teste, as cotas exibidas sejam corretas
    const { error: poolResetError } = await supabase
      .from('quota_pools')
      .update({ booked_count: 0, updated_at: new Date().toISOString() })
      .eq('municipality_id', municipalityId);

    if (poolResetError) console.error('[DB] Erro ao resetar booked_count dos pools:', poolResetError);

    // 3. Delete fiscal infrastructure (audit logs first due to FK)
    await supabase.from('historico_auditoria').delete().eq('operador_id', 0); // Temporary simplified logic
    await supabase.from('protocolos_fiscais').delete().eq('municipio_id', municipalityId);

    // 4. Delete appointments
    const { error: appoError } = await supabase
      .from('appointments')
      .delete()
      .eq('municipality_id', municipalityId);

    if (appoError) throw appoError;

    // 5. Delete patients
    const { error: patientError } = await supabase
      .from('patients')
      .delete()
      .eq('municipality_id', municipalityId);

    if (patientError) throw patientError;

    // Refresh local data
    this.refreshPatientData(municipalityId);
  }


  async deleteLocation(id: string) {
    return this.quotaService.deleteLocation(id);
  }


  async getProcedureLocationByName(name: string, municipalityId?: string): Promise<any | null> {
    return this.quotaService.getProcedureLocationByName(name, municipalityId);
  }

  async getAllUnfilledSlots(): Promise<any[]> {
    // Usa dashboardMunicipalityFilter se disponível, senão usa currentMunicipalityId
    const munId = this.dashboardMunicipalityFilter() || this.currentMunicipalityId();
    const date = this.dashboardDate();
    return this.quotaService.getAllUnfilledSlots(munId, date);
  }

  // --- MÉTODOS ADMINISTRATIVOS ---

  /**
   * Limpa dados transacionais e cadastrais.
   * CUIDADO: Esta operação é destrutiva.
   */
  async clearSystemData() {
    console.warn('[DB] Iniciando limpeza total do sistema...');
    try {
      // Ordem de deleção para respeitar chaves estrangeiras
      const tables = [
        'agenda_slots',
        'patient_history',
        'appointments',
        'patients',
        'system_logs',
        'quota_pools',
        'executors',
        'procedure_locations',
        'procedures'
      ];

      for (const table of tables) {
        const { error } = await supabase.from(table).delete().neq('id', -1); // Deleta tudo
        if (error) throw error;
        console.log(`[DB] Tabela ${table} limpa.`);
      }

      this.toast.showSuccess('Limpeza concluída com sucesso.');
      // Recarregar dados locais
      await this.loadBusinessData(this.currentMunicipalityId());
    } catch (err) {
      console.error('[DB] Erro na limpeza:', err);
      this.toast.showError(err, 'Erro ao realizar limpeza dos dados');
    }
  }

  /**
   * Duplica um procedimento existente.
   */
  async duplicateProcedure(id: number, newName?: string) {
    const inserted = await this.quotaService.duplicateProcedure(id, this.currentMunicipalityId(), newName);
    if (inserted) {
      await this.loadBusinessData(this.currentMunicipalityId());
    }
    return inserted;
  }

  /**
   * Duplica um município existente e todas as suas configurações.
   */
  async duplicateMunicipality(sourceId: string, newName: string) {
    try {
      await this.municipalityService.duplicateMunicipality(sourceId, newName);
      await this.loadBusinessData(this.currentMunicipalityId());
    } catch (err) {
      // Erro já tratado no serviço
    }
  }

  /** Dispara o reload da fila paginada */
  triggerQueueRefresh() {
    this.apptService.triggerQueueRefresh();
  }

  /**
   * Computed que une os dados de ciclos cirúrgicos do AppointmentService.
   */
  readonly surgicalMonitoringList = computed(() => {
    return this.apptService.surgicalCyclesData();
  });

  // --- FACADE: QuotaService methods used by components ---

  async getQuotaPools(municipalityId: string, monthRef: string) {
    return this.quotaService.refreshQuotaPools(municipalityId, monthRef);
  }

  async getPoolSlots(poolId: string) {
    return this.quotaService.getPoolSlots(poolId);
  }

  async getPoolQuotas(procedureId: number, municipalityId: string, monthRef: string) {
    return this.quotaService.getPoolQuotas(procedureId, municipalityId, monthRef);
  }

  async getAvailableExecutorsWithQuotas(procedureId: number, municipalityId: string, monthRef?: string) {
    return this.quotaService.getAvailableExecutorsWithQuotas(procedureId, municipalityId, monthRef);
  }

  async getAvailableMonthsWithQuotas(procedureId: number, municipalityId: string) {
    return this.quotaService.getAvailableMonthsWithQuotas(procedureId, municipalityId);
  }

  async getAvailableSlots(procedureId: number, municipalityId: string, monthRef?: string) {
    return this.quotaService.getAvailableSlots(procedureId, municipalityId, monthRef);
  }

  async createMonthlyPool(data: Partial<QuotaPool>) {
    return this.quotaService.createMonthlyPool(data);
  }

  async createQuotaPool(proc: Omit<Procedure, 'id' | 'latestProtocol' | 'protocolHistory'>, initialLimit: number, municipalityId: string, monthRef: string) {
    return this.quotaService.createQuotaPool(proc, initialLimit, municipalityId, monthRef);
  }

  async updateQuotaPoolLimit(poolId: string, newLimit: number) {
    return this.quotaService.updateQuotaPoolLimit(poolId, newLimit);
  }



  async clearPoolForRewrite(poolId: string): Promise<number> {
    return this.quotaService.clearPoolForRewrite(poolId);
  }

  async distributeSimpleQuota(poolId: string, executorId: string, municipalityId: string, count: number, parentProcedureId?: number) {
    return this.quotaService.distributeSimpleQuota(poolId, executorId, municipalityId, count, parentProcedureId);
  }

  /**
   * Distribui vagas para múltiplos executores simultaneamente.
   * @param parentProcedureId ID da cirurgia pai (para vagas de pré-operatório)
   */
  async distributeMultiExecutor(
    poolId: string,
    distributions: { executorId: string; quantity: number }[],
    municipalityId: string,
    autoRepeat: boolean = false,
    parentProcedureId?: number
  ) {
    return this.quotaService.distributeMultiExecutor(poolId, distributions, municipalityId, autoRepeat, parentProcedureId);
  }

  /**
   * Replica as distribuições de template para o mês alvo.
   */
  async replicateDistributions(municipalityId: string, targetMonth: string) {
    return this.quotaService.replicateDistributions(municipalityId, targetMonth);
  }

  /**
   * Busca templates de distribuição para um pool.
   */
  async getDistributionTemplates(poolId: string) {
    return this.quotaService.getDistributionTemplates(poolId);
  }

  async getDistributionsByPools(poolIds: string[]) {
    return this.quotaService.getDistributionsByPools(poolIds);
  }

  async deleteAvailableSlotsByPool(poolId: string) {
    return this.quotaService.deleteAvailableSlotsByPool(poolId);
  }

  async deleteSlot(slotId: string) {
    // Deleta um slot individual de agenda
    const { error } = await supabase.from('agenda_slots').delete().eq('id', slotId);
    if (error) throw error;
  }

  async bookManualSlot(poolId: string | null | undefined, municipalityId: string, booking: {
    patientId: number,
    appointmentId: number,
    location: string,
    startTime: string,
    executorId?: string | number | null,
    isUbsOnDemand?: boolean,
    parentProcedureId?: number | null,
    procedureId?: number | null,
    excludedSlotIds?: string[]
  }) {
    return this.quotaService.bookManualSlot(poolId, municipalityId, booking);
  }

  async createUbsAdHocSlot(appointment: any, formVal: any): Promise<void> {
    return this.quotaService.createUbsAdHocSlot(appointment, formVal);
  }

  /**
   * Returns how many appointments are booked/realizado at a given UBS location
   * for the current week and month, plus the weekly/monthly capacity from the location config.
   */
  async getUbsScheduledStats(
    locationName: string,
    municipalityId: string,
    weeklyCapacity: number = 0
  ): Promise<{ week: number; month: number; weekCap: number; monthCap: number }> {
    const now = new Date();

    // Start of current week (Monday)
    const dayOfWeek = now.getDay() || 7;
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - (dayOfWeek - 1));
    weekStart.setHours(0, 0, 0, 0);

    // Week end (Sunday)
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    // Month bounds
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

    const weekStartISO = weekStart.toISOString().split('T')[0];
    const weekEndISO = weekEnd.toISOString().split('T')[0];
    const monthStartISO = monthStart.toISOString().split('T')[0];
    const monthEndISO = monthEnd.toISOString().split('T')[0];

    // Count weeks in current month for capacity calculation
    const weeksInMonth = Math.ceil((monthEnd.getDate()) / 7);
    const monthCap = weeklyCapacity > 0 ? weeklyCapacity * weeksInMonth : 0;

    // Use common ILIKE location filter
    const locFilter = locationName.trim();

    // Query source of truth: appointments table
    // We count: (Agendado OR Realizado) AND (Location matches) AND (Date in range)
    const { data: apps, error } = await supabase
      .from('appointments')
      .select('id, status, schedule_date, outcome_date')
      .eq('municipality_id', municipalityId)
      .eq('is_active', true)
      .or(`status.eq.Agendado,status.eq.Realizado`)
      .or(`schedule_location.ilike."%${locFilter}%",outcome_location.ilike."%${locFilter}%"`);

    if (error) {
      console.error('[DatabaseService] Error in getUbsScheduledStats:', error);
      return { week: 0, month: 0, weekCap: weeklyCapacity, monthCap };
    }

    const filtered = (apps || []).filter(a => {
      // Use schedule_date for Agendado, outcome_date for Realizado
      const dateStr = a.status === 'Agendado' ? a.schedule_date : a.outcome_date;
      if (!dateStr) return false;
      return dateStr >= monthStartISO && dateStr <= monthEndISO;
    });

    const weekCount = filtered.filter(a => {
      const dateStr = a.status === 'Agendado' ? a.schedule_date : a.outcome_date;
      return dateStr >= weekStartISO && dateStr <= weekEndISO;
    }).length;

    const monthCount = filtered.length;

    console.log(`[DatabaseService] UBS Metrics for "${locationName}": ${weekCount}/${monthCount}`);

    return {
      week: weekCount,
      month: monthCount,
      weekCap: weeklyCapacity,
      monthCap
    };
  }

  async getLocations(municipalityId: string) {
    return this.quotaService.getLocations(municipalityId);
  }

  async addLocation(loc: Omit<ProcedureLocation, 'id' | 'isActive'>) {
    return this.quotaService.addLocation(loc);
  }

  async updateLocation(id: string, updates: Partial<ProcedureLocation>, oldName?: string) {
    return this.quotaService.updateLocation(id, updates, oldName);
  }

  async getExecutors(municipalityId: string) {
    return this.quotaService.getExecutors(municipalityId);
  }

  async createExecutor(municipalityId: string, name: string) {
    return this.quotaService.createExecutor(municipalityId, name);
  }

  async updateExecutor(id: string, name: string) {
    return this.quotaService.updateExecutor(id, name);
  }

  async deleteExecutor(id: string) {
    return this.quotaService.deleteExecutor(id);
  }

  async getProcedureLocations(municipalityId?: string) {
    return this.quotaService.getProcedureLocations(municipalityId);
  }

  async deleteProcedure(idStr: string, procedureId: number) {
    return this.quotaService.deleteProcedure(idStr, procedureId);
  }

  /**
   * Exclui TODAS as vagas distribuídas de um pool.
   */
  async deleteAllDistributedSlots(poolId: string) {
    return this.quotaService.deleteAllDistributedSlots(poolId);
  }

  // --- FACADE: OperatorService methods ---

  async updateOperator(id: number, updates: Partial<Operator> & { password?: string, location_id?: number }) {
    return this.operatorService.updateOperator(id, updates);
  }

  async getOperators() {
    return this.operatorService.loadOperators();
  }

  // --- FACADE: AppointmentService methods ---

  async updateAppointment(
    updatedApp: Appointment,
    operatorId: number,
    triggerWebhook: boolean = false,
    options?: { hideLoading?: boolean; silentToast?: boolean }
  ) {
    return this.apptService.updateAppointment(updatedApp, operatorId, triggerWebhook, options);
  }

  async resendAppointmentWebhook(appointment: Appointment): Promise<void> {
    return this.apptService.resendAppointmentWebhook(appointment);
  }

  mapToPatientProcess(p: Patient): PatientProcess {
    return this.apptService.mapToPatientProcess(p);
  }

  async getProcedureScheduledStatsOnDate(
    procedureId: number,
    scheduleDate: string,
    municipalityId?: string | null
  ) {
    return this.apptService.getProcedureScheduledStatsOnDate(procedureId, scheduleDate, municipalityId);
  }

  /**
   * Busca estatísticas diárias para o fluxo UBS On-Demand.
   * Retorna a cota diária do profissional e quantos agendamentos já existem.
   */
  async getUbsDailyStats(
    professionalId: string,
    municipalityId: string,
    locationId: string,
    date: string
  ): Promise<{ dayVacancies: number, dayScheduled: number }> {
    try {
      // 1. Buscar a cota semanal do profissional para esta unidade/município
      const { data: quotas, error: qError } = await supabase
        .from('professional_team_quotas')
        .select('daily_quota')
        .eq('professional_id', professionalId)
        .eq('location_id', locationId)
        .maybeSingle();

      if (qError) throw qError;

      // Cota diária do profissional para esta unidade
      const dayVacancies = quotas ? quotas.daily_quota : 0;

      // 2. Contar agendamentos para este profissional nesta data
      const { count, error: cError } = await supabase
        .from('appointments')
        .select('*', { count: 'exact', head: true })
        .eq('municipality_id', municipalityId)
        .eq('allocated_professional_id', professionalId)
        .eq('allocated_date', date)
        .eq('is_active', true)
        .or('status.eq.Agendado,status.eq.Realizado');

      if (cError) throw cError;

      return {
        dayVacancies: dayVacancies || 0,
        dayScheduled: count || 0
      };
    } catch (err) {
      console.error('[DatabaseService] Erro ao buscar métricas diárias UBS:', err);
      return { dayVacancies: 0, dayScheduled: 0 };
    }
  }

  /**
   * Dispara a verificação/limpeza de agendamentos estagnados (> 7 dias) em 'Em Agendamento'
   */
  async checkAndExpireStaleScheduling(days: number = 7): Promise<number> {
    return this.apptService.checkAndExpireStaleScheduling(days);
  }
}
